import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { captureTracking, getCheckoutUrl, getOrigem, recordCheckoutAttribution } from "../lib/tracking";
import { CookieConsent } from "../components/CookieConsent";
import { AudioPlayer } from "../components/AudioPlayer";
import { OfferCountdown } from "../components/OfferCountdown";
import {
  ArrowRight, Check, Headphones, BookOpen, ListChecks, ScanLine,
  Play, ChevronDown, Sparkles, ShieldCheck, HeartHandshake,
} from "lucide-react";

const HERO_VARIANT = "vsl";
const VSL_SRC = `${process.env.PUBLIC_URL || ""}/assets/vsl-v2.mp4`;
const VSL_POSTER = `${process.env.PUBLIC_URL || ""}/assets/vsl-capa-casal.webp`;
const HERO_IMG = `${process.env.PUBLIC_URL || ""}/assets/hero-mulher-oracao-v2.webp`;
const PREVIEWS = [
  { src: "/assets/preview-sumario.webp", label: "Sumário" },
  { src: "/assets/preview-introducao.webp", label: "Introdução" },
  { src: "/assets/preview-como-usar.webp", label: "Como usar" },
  { src: "/assets/preview-capitulo.webp", label: "Capítulos" },
  { src: "/assets/preview-exercicio.webp", label: "Exercícios" },
];

const RECEIVE = [
  { icon: BookOpen, title: "E-book completo", text: "Um guia organizado para ajudar você a desenvolver seus momentos de oração." },
  { icon: Headphones, title: "50 orações em áudio", text: "Quando não souber o que dizer, escolha uma oração, dê o play e acompanhe no seu tempo." },
  { icon: ListChecks, title: "Método prático + exercícios", text: "Pequenos exercícios para ajudar a transformar a oração em parte da sua rotina." },
  { icon: HeartHandshake, title: "Orações para diferentes momentos", text: "Encontre palavras para agradecer, pedir força, buscar paz, orar pela família e muito mais." },
];

const HOW = [
  { n: 1, icon: BookOpen, title: "Abra o e-book", text: "Escolha a oração que combina com o momento que você está vivendo." },
  { n: 2, icon: ScanLine, title: "Escaneie o QR Code", text: "Use a câmera do celular. Não precisa instalar nenhum aplicativo." },
  { n: 3, icon: Play, title: "Dê o play", text: "Ouça a oração onde estiver e faça desse momento o seu encontro com Deus." },
];

// Títulos reais dos capítulos (extraídos do sumário do e-book)
const CHAPTERS = [
  "A verdade sobre não saber o que falar",
  "O que muda quando existe método",
  "Antes de começar: o mínimo necessário",
  "Passo 1 — Escolher o ponto de partida",
  "Passo 2 — Montar guia de oração",
  "Passo 3 — A primeira semana na prática",
  "Passo 4 — Medir o que importa",
  "Passo 5 — Ajustar sem recomeçar",
  "Os erros que custam caro",
  "Manter o ritmo sem depender de motivação",
  "Da primeira semana constante ao próximo nível",
];

const MOMENTS = [
  "Quando eu não souber o que dizer", "Para começar o dia", "Antes de dormir",
  "Quando eu estiver ansioso(a)", "Pela minha família", "Pelos meus filhos",
  "Quando eu pensar em desistir", "Para pedir perdão", "Quando eu estiver com medo",
  "Para agradecer", "Quando eu precisar de paz", "Quando eu só quiser conversar com Deus",
];

const EMOTIONAL = [
  "Quando a ansiedade apertar", "Antes de dormir", "Ao começar o dia", "Pela família",
  "Pelos filhos", "Para agradecer", "Quando estiver com medo", "Quando precisar de paz",
];

// Preencher com depoimentos REAIS. Enquanto vazio, a seção fica oculta.
const TESTIMONIALS = [];

const FAQS = [
  { q: "O produto é físico?", a: "Não. O produto é 100% digital." },
  { q: "É seguro comprar?", a: "Sim. O pagamento é processado pela Monetizze, uma das maiores plataformas de produtos digitais do Brasil, em ambiente seguro. E você ainda conta com a garantia de 7 dias." },
  { q: "Serve para quem nunca ora?", a: "Sim, especialmente. O material foi pensado para quem quer começar ou retomar a oração de forma simples — com um passo a passo e orações prontas para diferentes momentos, mesmo que você nunca tenha orado antes." },
  { q: "Como recebo o e-book?", a: "O acesso é disponibilizado digitalmente após a confirmação da compra." },
  { q: "O que exatamente eu recebo depois da compra?", a: "Você recebe acesso ao e-book digital “Quando Faltam Palavras”, aos exercícios práticos e às 50 orações em áudio. O material explica de forma simples como acessar e ouvir as orações pelo celular." },
  { q: "Preciso instalar algum aplicativo?", a: "Não. O conteúdo pode ser acessado pelo navegador do celular." },
  { q: "Como funcionam as 50 orações em áudio?", a: "As orações possuem acesso por QR Code. Basta apontar a câmera do celular e acessar a oração correspondente." },
  { q: "Posso ouvir pelo celular?", a: "Sim." },
  { q: "O pagamento é mensal?", a: "Não. O pagamento de R$ 29,90 é único." },
  { q: "Quando recebo o material?", a: "Após a confirmação do pagamento." },
  { q: "Existe garantia?", a: "Sim. Garantia de 7 dias, conforme as condições aplicáveis à compra." },
];

function trackEvent(name, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch (e) { /* noop */ }
}

// Rótulo da conversão "Clique para Checkout" do Google Ads. AGUARDANDO label do usuário.
// NÃO usar os labels de InitiateCheckout (MnVtCLLsxvscEJar4uFE) nem Purchase (j1D6CNWbwvscEJar4uFE).
const GADS_CONVERSION_LABEL = "";

// Dispara eventos de INTENÇÃO de checkout no clique do CTA (NÃO é Purchase, NÃO duplica a Monetizze).
function fireCheckoutEvents(testid) {
  const value = 29.90;
  const currency = "BRL";
  try {
    // Google Ads: evento próprio da landing ("Clique para Checkout"). Só conta como conversão quando o label for fornecido.
    if (typeof window.gtag === "function" && GADS_CONVERSION_LABEL) {
      window.gtag("event", "conversion", { send_to: `AW-18458645910/${GADS_CONVERSION_LABEL}`, value, currency, cta: testid });
    }
    // Meta: evento CUSTOMIZADO de saída para o checkout (o InitiateCheckout/Purchase fica por conta da Monetizze).
    if (typeof window.fbq === "function") {
      window.fbq("trackCustom", "CheckoutOutbound", { value, currency, content_name: "Quando Faltam Palavras", cta: testid });
    }
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture("checkout_outbound", { cta: testid, value });
    }
  } catch (e) { /* noop */ }
}

function CTA({ href, testid, label, onClick, className = "" }) {
  return (
    <a
      href={href || "#"}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      onClick={(e) => { e.currentTarget.href = getCheckoutUrl(); recordCheckoutAttribution(process.env.REACT_APP_BACKEND_URL); trackEvent("CTA_Click", { cta: testid }); fireCheckoutEvents(testid); onClick && onClick(); }}
      className={`cta-shine inline-flex items-center justify-center gap-2 rounded-full bg-[#C5A059] text-[#2A1D16] font-bold tracking-wide shadow-lg shadow-[#C5A059]/30 hover:bg-[#B08B44] hover:scale-[1.02] active:scale-[0.99] transition-all ${className}`}
    >
      {label} <ArrowRight className="w-5 h-5 shrink-0" />
    </a>
  );
}

const Eyebrow = ({ children }) => (
  <span className="font-cinzel uppercase tracking-[0.26em] text-[#C5A059] text-[11px] sm:text-xs">{children}</span>
);

// Preço "de" (ancoragem) — preço original antes da oferta de lançamento.
const OLD_PRICE = "R$ 39,90";

function fmtDur(t) {
  if (!t || !isFinite(t)) return "";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Selo de compra segura processada pela Monetizze
function SafeSeal({ dark = false, className = "" }) {
  return (
    <div
      data-testid="monetizze-safe-seal"
      className={`inline-flex items-center gap-2.5 rounded-xl px-3.5 py-2 border ${
        dark
          ? "bg-[#F7EFE0]/10 border-[#F7EFE0]/20 text-[#EADFCB]"
          : "bg-[#FAF7F2] border-[#E5DCCF] text-[#6E5A4F]"
      } ${className}`}
    >
      <span className={`inline-flex w-8 h-8 rounded-lg items-center justify-center shrink-0 ${dark ? "bg-[#4C7A5A]/25" : "bg-[#4C7A5A]/12"}`}>
        <ShieldCheck className="w-5 h-5 text-[#4C7A5A]" strokeWidth={2} />
      </span>
      <span className="text-left leading-tight">
        <span className={`block text-xs font-bold ${dark ? "text-[#F7EFE0]" : "text-[#3A2921]"}`}>Compra 100% segura</span>
        <span className="block text-[10px] uppercase tracking-widest">Processado por Monetizze</span>
      </span>
    </div>
  );
}

// Versão anterior do 1º bloco (foto), preservada para comparação/reversão.
function HeroFoto({ price, checkoutUrl }) {
  return (
    <section className="relative overflow-hidden">
      <div className="sun-rays pointer-events-none" aria-hidden="true" />
      <div className="sun-glow" aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-5 pt-7 pb-12 sm:pt-10 md:pt-14 sm:pb-20 grid md:grid-cols-2 gap-5 md:gap-12 items-center relative z-10">
        <p data-testid="v3-hero-mobile-tagline" className="md:hidden order-first text-center font-serif-display font-semibold text-[24px] sm:text-3xl leading-[1.15] text-[#3A2921] max-w-[340px] mx-auto">
          50 orações para os momentos em que <span className="text-[#B08B44] italic">as palavras não vêm.</span>
        </p>
        <div className="contents md:block md:order-1 md:-translate-y-5 text-center md:text-left">
          <div className="order-3 text-center md:text-left">
          <Eyebrow>E-book Devocional • 50 Orações em Áudio</Eyebrow>
          <h1 className="font-serif-display font-semibold leading-[1.12] text-[26px] sm:text-4xl lg:text-5xl mt-3 sm:mt-4">
            Você quer falar com Deus… mas às vezes simplesmente não sabe o que dizer?
          </h1>
          <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#4A362B] max-w-md mx-auto md:mx-0">
            {["E-book completo", "Exercícios práticos", "50 orações em áudio", "Acesso imediato"].map((f, i) => (
              <li key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4C7A5A] shrink-0" strokeWidth={2.5} />{f}</li>
            ))}
          </ul>
          </div>

          <div className="order-2 md:mt-6 flex flex-col items-center md:items-start gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F4EbD8] text-[#8a6d2f] text-[11px] font-bold uppercase tracking-widest px-3 py-1 border border-[#E1CE9F]">
              <Sparkles className="w-3.5 h-3.5" /> Oferta de lançamento
            </span>
            <div data-testid="v3-hero-price-block" className="flex flex-col items-center md:items-start leading-none">
              <span data-testid="v3-hero-old-price" className="text-lg sm:text-xl text-[#B9A48F] line-through decoration-[#B9A48F]/80">de {OLD_PRICE}</span>
              <div className="flex items-end gap-2 mt-1">
                <span className="text-sm text-[#8C7565] lowercase pb-3">por</span>
                <span data-testid="v3-hero-price" className="hero-gold-price font-serif-display font-bold text-[60px] sm:text-[84px] lg:text-[96px] tracking-tight">{price}</span>
              </div>
              <span className="text-xs text-[#8C7565] uppercase tracking-[0.2em] mt-1">pagamento único</span>
            </div>
            <OfferCountdown />
            <CTA href={checkoutUrl} testid="v3-cta-hero" label="QUERO COMEÇAR AGORA" className="hero-gold-btn mt-1 px-9 py-[18px] sm:py-5 text-base sm:text-lg w-full sm:w-auto max-w-md" />
            <p className="flex flex-wrap items-center justify-center md:justify-start gap-x-2 text-xs text-[#8C7565]">
              <ShieldCheck className="w-3.5 h-3.5 text-[#4C7A5A]" /> Pagamento seguro <span className="text-[#D8CBB8]">•</span> Acesso imediato <span className="text-[#D8CBB8]">•</span> Garantia de 7 dias
            </p>
            <SafeSeal className="mt-1" />
          </div>
        </div>

        <div className="order-1 md:order-2 relative flex justify-center md:justify-end -mx-5 md:mx-0 -mt-3 md:mt-0 md:-translate-y-14 md:translate-x-10 xl:translate-x-20">
          <div className="hero-photo-glow" aria-hidden="true" />
          <img src={HERO_IMG} alt="Mulher em oração, de olhos fechados e mãos unidas" data-testid="v3-hero-image" width="848" height="1264" fetchpriority="high" decoding="async" className="hero-photo relative z-10 w-[316px] sm:w-full sm:max-w-[506px] md:max-w-[520px] h-[413px] sm:h-[572px] md:h-[640px] md:scale-110 xl:scale-[1.265] md:origin-right object-cover object-[50%_70%]" />
        </div>
      </div>
    </section>
  );
}

function HeroVSL({ price, checkoutUrl }) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const play = () => { setStarted(true); videoRef.current?.play().catch(() => {}); };
  const onPlay = () => { if (!started) setStarted(true); trackEvent("VSL_Play", { page: "teste2" }); };
  return (
    <section data-testid="v3-hero-vsl" className="relative overflow-hidden">
      <div className="sun-rays pointer-events-none" aria-hidden="true" />
      <div className="sun-glow" aria-hidden="true" />
      <div className="max-w-3xl mx-auto px-4 sm:px-5 pt-6 pb-10 sm:pt-10 sm:pb-16 relative z-10 text-center">
        <h1 data-testid="v3-hero-title" className="font-serif-display font-semibold leading-[1.1] text-[29px] sm:text-5xl lg:text-[56px] max-w-2xl mx-auto">
          Quando faltam palavras, <span className="text-[#B08B44] italic">aprenda a falar com Deus.</span>
        </h1>
        <p data-testid="v3-hero-subtitle" className="text-[#6E5A4F] text-[15px] sm:text-lg leading-snug mt-2 sm:mt-3 max-w-xl mx-auto">
          Assista a este vídeo e descubra como começar uma conversa mais próxima e sincera com Deus.
        </p>

        <div className="relative mt-4 sm:mt-5 -mx-1 sm:mx-auto sm:max-w-2xl rounded-2xl overflow-hidden bg-[#2A1D16] shadow-[0_20px_50px_-20px_rgba(58,41,33,0.55)] ring-1 ring-[#E1CE9F] aspect-video">
          <video
            ref={videoRef}
            data-testid="v3-vsl-video"
            poster={VSL_POSTER}
            preload="none"
            playsInline
            controls={started}
            onPlay={onPlay}
            className="absolute inset-0 w-full h-full object-contain bg-black"
          >
            <source src={VSL_SRC} type="video/mp4" />
            <source src={VSL_SRC.replace(".mp4", ".webm")} type="video/webm" />
          </video>
          {!started && (
            <button type="button" onClick={play} data-testid="v3-vsl-play-btn" aria-label="Assistir ao vídeo" className="group absolute inset-0 bg-black/15 hover:bg-black/5 transition-colors duration-300">
              <span data-testid="v3-vsl-play-circle" className="absolute left-1/2 top-[calc(50%+40px)] -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[54px] h-[54px] sm:w-[72px] sm:h-[72px] aspect-square rounded-full hero-gold-btn shadow-xl transition-transform duration-300 group-hover:scale-105">
                <span className="vsl-soft-pulse absolute inset-0 rounded-full" aria-hidden="true" />
                <Play className="relative w-6 h-6 sm:w-[30px] sm:h-[30px] ml-[3px] fill-current" />
              </span>
            </button>
          )}
        </div>

        <div className="mt-4 sm:mt-4 flex flex-col items-center gap-2.5">
          <span data-testid="v3-hero-offer-label" className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.25em] text-[#8a6d2f]">Oferta especial</span>
          <div data-testid="v3-hero-price-block" className="flex flex-col items-center leading-none">
            <span data-testid="v3-hero-old-price" className="text-base sm:text-lg text-[#B9A48F] line-through decoration-[#B9A48F]/80">de {OLD_PRICE}</span>
            <div className="flex items-end gap-2 mt-1">
              <span className="text-sm text-[#8C7565] lowercase pb-2.5">por</span>
              <span data-testid="v3-hero-price" className="hero-gold-price font-serif-display font-bold text-[56px] sm:text-[80px] tracking-tight">{price}</span>
            </div>
          </div>
          <CTA href={checkoutUrl} testid="v3-cta-hero" label="QUERO APRENDER A FALAR COM DEUS — COMPRAR AGORA" className="hero-gold-btn mt-1 px-6 sm:px-10 py-[18px] sm:py-5 text-[14px] sm:text-lg leading-snug w-full max-w-xl sm:max-w-2xl" />
          <ul data-testid="v3-hero-info" className="mt-1 space-y-1 text-xs sm:text-sm text-[#8C7565]">
            <li className="flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4C7A5A]" strokeWidth={2.5} /> 50 orações + 50 áudios + exercícios práticos</li>
            <li className="flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4C7A5A]" strokeWidth={2.5} /> Acesso digital imediato após a confirmação do pagamento</li>
            <li className="flex items-center justify-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#4C7A5A]" /> Pagamento seguro pela Monetizze • Garantia de 7 dias</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

export default function LandingV3() {
  const [prayer, setPrayer] = useState(null);
  const [price, setPrice] = useState("R$ 29,90");
  const [audioDuration, setAudioDuration] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [checkoutUrl, setCheckoutUrl] = useState(getCheckoutUrl());
  const audioTracked = useRef(false);

  const audioSrc = `${api.defaults.baseURL}/prayers/1/audio`;

  useEffect(() => {
    captureTracking();
    setCheckoutUrl(getCheckoutUrl());
    trackEvent("LandingPageView", { origem: getOrigem(), page: "teste2" });
    api.post("/track/visit", { origem: getOrigem() }).catch(() => {});
    api.get("/prayers/1").then((r) => setPrayer(r.data)).catch(() => {});
    api.get("/config").then((r) => r.data?.price && setPrice(r.data.price)).catch(() => {});
  }, []);

  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("is-visible")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const onAudioPlay = () => {
    if (!audioTracked.current) { audioTracked.current = true; trackEvent("AudioDemoPlay", { page: "teste2" }); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#3A2921] font-sans-body pb-24 sm:pb-0">
      <CookieConsent />
      {HERO_VARIANT === "vsl" ? <HeroVSL price={price} checkoutUrl={checkoutUrl} /> : <HeroFoto price={price} checkoutUrl={checkoutUrl} />}

      {/* ===== 2. IDENTIFICAÇÃO EMOCIONAL ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-12 sm:py-16">
        <div className="reveal max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold leading-snug text-[#3A2921]">
            Para aqueles momentos em que você precisa falar com Deus, mas as palavras não vêm.
          </h2>
          <p className="text-[#6E5A4F] mt-5 leading-relaxed max-w-2xl mx-auto">
            Talvez você queira agradecer, pedir orientação, encontrar paz ou simplesmente colocar para fora aquilo que está sentindo. <strong className="font-semibold text-[#3A2921]">“Quando Faltam Palavras”</strong> foi criado para ajudar você a começar essa conversa de forma simples e natural.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-7">
            {EMOTIONAL.map((t, i) => (
              <span key={i} className="text-sm rounded-full bg-[#FDFBF7] border border-[#E5DCCF] px-3.5 py-1.5 text-[#6E5A4F]">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3. DEMONSTRAÇÃO DO ÁUDIO + 4. CTA APÓS O ÁUDIO ===== */}
      <section className="bg-[#2A1D16] text-[#F7EFE0] py-12 sm:py-18">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <span className="font-cinzel uppercase tracking-[0.26em] text-[#D8B36A] text-[11px] sm:text-xs">Experimente antes de decidir</span>
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold mt-3">Antes de decidir, ouça uma das orações</h2>
          <p className="text-[#EADFCB] mt-3 leading-relaxed">Dê o play. Respire fundo. Este é apenas um pequeno exemplo das 50 orações que acompanham o e-book.</p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C5A059] text-[#2A1D16] text-xs font-bold uppercase tracking-widest px-3.5 py-1.5">
              <Play className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} /> Prévia gratuita
            </span>
            {audioDuration > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F7EFE0]/10 border border-[#F7EFE0]/20 text-[#EADFCB] text-xs font-semibold px-3 py-1.5">
                Duração {fmtDur(audioDuration)}
              </span>
            )}
          </div>
          <div className="mt-5 bg-[#F7F2E9] rounded-3xl p-4 sm:p-5 shadow-xl" onPointerDown={onAudioPlay}>
            <div className="text-[#8C7565] text-xs uppercase tracking-widest font-cinzel mb-3 px-1">Oração 01 — {prayer?.title || "Quando eu não souber o que dizer"}</div>
            <AudioPlayer src={audioSrc} hasAudio={!!prayer?.has_audio} onDuration={setAudioDuration} preload="auto" />
          </div>

          {/* CTA imediatamente após o áudio */}
          <div className="mt-9 border-t border-[#F7EFE0]/15 pt-8">
            <h3 className="font-serif-display text-2xl sm:text-3xl font-semibold">Gostou desse momento?</h3>
            <p className="text-[#EADFCB] mt-3 max-w-md mx-auto leading-relaxed">Tenha acesso às <strong className="text-[#F7EFE0]">50 orações em áudio</strong>, ao e-book completo e aos exercícios práticos por apenas <span className="whitespace-nowrap">{price}</span>.</p>
            <div className="mt-6"><CTA href={checkoutUrl} testid="v3-cta-audio" label={"QUERO ACESSAR AS 50 ORAÇÕES — R$\u00A029,90"} className="px-7 py-4 text-sm sm:text-base w-full sm:w-auto" /></div>
            <p className="text-xs text-[#EADFCB]/80 mt-4">Pagamento único • Acesso imediato • Garantia de 7 dias</p>
          </div>
        </div>
      </section>

      {/* ===== 5. O QUE VOCÊ RECEBE ===== */}
      <section className="max-w-5xl mx-auto px-5 py-12 sm:py-18">
        <div className="reveal text-center mb-9">
          <Eyebrow>Tudo o que está incluído</Eyebrow>
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold mt-3">O que você recebe</h2>
        </div>
        <div className="reveal grid sm:grid-cols-2 gap-4 sm:gap-5">
          {RECEIVE.map((c, i) => (
            <div key={i} className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-5 sm:p-6 flex gap-4 items-start hover:shadow-md transition-shadow">
              <span className="inline-flex w-12 h-12 rounded-full bg-[#F4EFE6] border border-[#E5DCCF] items-center justify-center shrink-0">
                <c.icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1.6} />
              </span>
              <div>
                <h3 className="font-serif-display text-lg sm:text-xl font-semibold">{c.title}</h3>
                <p className="text-sm text-[#6E5A4F] mt-1.5 leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-9 flex flex-col items-center gap-2">
          <CTA href={checkoutUrl} testid="v3-cta-receive" label={"QUERO TER ACESSO POR R$\u00A029,90"} className="px-8 py-4 text-base w-full sm:w-auto" />
          <span className="text-xs text-[#8C7565]">Pagamento único • Acesso imediato</span>
        </div>
      </section>

      {/* ===== 6. VEJA POR DENTRO ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-12 sm:py-18">
        <div className="reveal max-w-6xl mx-auto px-5 text-center">
          <Eyebrow>Espie por dentro</Eyebrow>
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold mt-3">Veja por dentro o que você vai receber</h2>
          <p className="text-[#6E5A4F] mt-3 max-w-xl mx-auto">Um material criado para ser simples de usar, agradável de ler e fácil de incorporar à sua rotina.</p>
          <div className="flex gap-4 overflow-x-auto pb-4 mt-9 snap-x sm:justify-center">
            {PREVIEWS.map((p, i) => (
              <figure key={i} className="shrink-0 snap-center">
                <img src={p.src} alt={`Página: ${p.label}`} loading="lazy" className="w-36 sm:w-44 rounded-xl border border-[#E5DCCF] shadow-md bg-white" />
                <figcaption className="text-xs uppercase tracking-widest text-[#9C877A] mt-3">{p.label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6.1 O QUE VOCÊ VAI ENCONTRAR (mini-índice) ===== */}
      <section className="max-w-3xl mx-auto px-5 py-12 sm:py-18">
        <div className="reveal text-center">
          <Eyebrow>Conteúdo do e-book</Eyebrow>
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold mt-3">O que você vai encontrar</h2>
          <p className="text-[#6E5A4F] mt-3 max-w-xl mx-auto">Um passo a passo simples, do começo ao ritmo constante — capítulo por capítulo.</p>
        </div>
        <ol className="reveal mt-9 space-y-2.5">
          {CHAPTERS.map((c, i) => (
            <li key={i} className="flex items-center gap-4 bg-[#FAF7F2] rounded-xl border border-[#E5DCCF] px-4 py-3 hover:border-[#C5A059]/60 transition-colors">
              <span className="font-cinzel text-[#C5A059] text-lg font-bold w-8 shrink-0 text-center">{String(i + 1).padStart(2, "0")}</span>
              <span className="w-px h-6 bg-[#E5DCCF] shrink-0" />
              <span className="text-[#4A362B] text-sm sm:text-base">{c}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* ===== 7. COMO FUNCIONA ===== */}
      <section className="max-w-5xl mx-auto px-5 py-12 sm:py-18 text-center">
        <Eyebrow>Simples no dia a dia</Eyebrow>
        <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold mt-3">Simples de usar no seu dia a dia</h2>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mt-9 text-left">
          {HOW.map((s) => (
            <div key={s.n} className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex w-10 h-10 rounded-full bg-[#3A2921] text-[#F7EFE0] items-center justify-center font-cinzel font-bold">{s.n}</span>
                <s.icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1.6} />
              </div>
              <h3 className="font-serif-display text-lg sm:text-xl font-semibold">{s.title}</h3>
              <p className="text-sm text-[#6E5A4F] mt-1.5 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 8. UMA ORAÇÃO PARA CADA MOMENTO ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-12 sm:py-18">
        <div className="reveal max-w-4xl mx-auto px-5 text-center">
          <Eyebrow>50 orações</Eyebrow>
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold mt-3">Uma oração para cada momento</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-9 text-left">
            {MOMENTS.map((m, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#FDFBF7] rounded-xl border border-[#E5DCCF] px-4 py-3">
                <span className="w-2 h-2 rounded-full bg-[#C5A059] shrink-0" />
                <span className="text-[#4A362B]">{m}</span>
              </div>
            ))}
          </div>
          <p className="font-serif-display text-2xl text-[#C5A059] mt-8">+ 38 outras orações</p>
          <p className="text-[#6E5A4F] mt-1">50 orações para acompanhar diferentes momentos da sua caminhada.</p>
        </div>
      </section>

      {/* ===== 9. PROVA SOCIAL (oculta até haver depoimentos reais) ===== */}
      {TESTIMONIALS.length > 0 && (
        <section className="max-w-5xl mx-auto px-5 py-12 sm:py-18 text-center">
          <Eyebrow>Prova social</Eyebrow>
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold mt-3">Quem já começou essa jornada</h2>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mt-9 text-left">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-6">
                <p className="text-[#4A362B] leading-relaxed">“{t.text}”</p>
                <p className="font-serif-display text-[#3A2921] mt-4">— {t.name}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== SOBRE O AUTOR (compacto) ===== */}
      <section className="max-w-3xl mx-auto px-5 py-10 sm:py-12">
        <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
          <div className="shrink-0 w-20 h-20 rounded-full bg-[#EADFCB] border border-[#D8C6A8] flex items-center justify-center">
            <span className="font-serif-display text-2xl text-[#8a6d2f]">FR</span>
          </div>
          <div>
            <Eyebrow>Sobre o autor</Eyebrow>
            <p className="text-[#6E5A4F] mt-2 leading-relaxed text-sm sm:text-base">
              “Quando Faltam Palavras” nasceu de uma ideia simples: muitas pessoas querem conversar com Deus, mas em alguns momentos não encontram palavras. Este material oferece um ponto de partida — com reflexões, exercícios e orações para diferentes momentos da vida. <span className="font-serif-display italic text-[#3A2921]">— Felipe Ramalho</span>
            </p>
          </div>
        </div>
      </section>

      {/* ===== 10. OFERTA FINAL ===== */}
      <section className="max-w-3xl mx-auto px-5 py-12 sm:py-18">
        <div className="reveal bg-[#2A1D16] text-[#F7EFE0] rounded-[2rem] p-8 sm:p-12 text-center relative overflow-hidden">
          <Sparkles className="absolute top-6 right-6 w-6 h-6 text-[#C5A059]/50" />
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold">Comece hoje a criar seu momento de oração</h2>
          <p className="text-[#EADFCB] mt-3 max-w-md mx-auto">Tenha sempre por perto um guia para aqueles momentos em que você quer falar com Deus, mas as palavras não vêm.</p>
          <ul className="mt-7 space-y-2.5 max-w-xs mx-auto text-left">
            {["E-book “Quando Faltam Palavras”", "Método prático de oração", "Exercícios guiados", "50 orações em áudio", "Acesso digital imediato"].map((f, i) => (
              <li key={i} className="flex items-center gap-3"><span className="inline-flex w-5 h-5 rounded-full bg-[#C5A059]/25 items-center justify-center shrink-0"><Check className="w-3 h-3 text-[#C5A059]" strokeWidth={3} /></span><span className="text-[#F3E9DB] text-sm">{f}</span></li>
            ))}
          </ul>
          <div className="mt-8 inline-flex flex-col items-center">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#D8B36A]">Oferta de lançamento</span>
            <span className="text-lg text-[#EADFCB]/60 line-through mt-1">de {OLD_PRICE}</span>
            <span className="font-serif-display text-5xl font-semibold">{price}</span>
            <span data-testid="v3-offer-single-payment" className="text-xs text-[#EADFCB]/80 uppercase tracking-wide mt-3">pagamento único • sem mensalidade</span>
          </div>
          <div className="mt-7"><CTA href={checkoutUrl} testid="v3-cta-final" label="QUERO COMEÇAR AGORA" className="px-9 py-4 text-base w-full sm:w-auto" /></div>
          <div className="mt-5 flex justify-center"><SafeSeal dark /></div>
          <p className="text-xs text-[#EADFCB]/80 mt-4 leading-relaxed">🔒 Pagamento seguro pela Monetizze<br />Acesso imediato após a confirmação • Garantia de 7 dias</p>
        </div>
      </section>

      {/* ===== GARANTIA ===== */}
      <section className="max-w-2xl mx-auto px-5 pb-12 sm:pb-18 text-center">
        <div className="reveal bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-8 flex flex-col items-center">
          <img src={`${process.env.PUBLIC_URL || ""}/assets/selo-garantia.png`} alt="Selo Compra Garantida — 7 dias — 100% Seguro" data-testid="v3-guarantee-seal" width="140" height="140" className="w-32 h-32 sm:w-36 sm:h-36 object-contain drop-shadow-md" />
          <h3 className="font-serif-display text-2xl font-semibold mt-3">Você tem 7 dias para conhecer o material</h3>
          <p className="text-[#6E5A4F] mt-2 leading-relaxed">Se dentro de 7 dias você sentir que não é para você, é só solicitar o reembolso conforme as condições da compra. Simples assim.</p>
        </div>
      </section>

      {/* ===== 11. FAQ ===== */}
      <section className="bg-[#F4EFE6] border-t border-[#E5DCCF] py-12 sm:py-18">
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-serif-display text-2xl sm:text-4xl font-semibold text-center mb-9">Perguntas frequentes</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="bg-[#FDFBF7] rounded-2xl border border-[#E5DCCF] overflow-hidden">
                <button data-testid={`v3-faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
                  <span className="font-serif-display text-lg text-[#3A2921]">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#C5A059] shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-5 text-[#6E5A4F] leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="text-center py-8 text-xs text-[#9C877A] space-y-2">
        <p>Quando Faltam Palavras · Acesso digital · Pagamento único</p>
        <p className="flex items-center justify-center gap-3">
          <a href="/privacidade" data-testid="footer-privacy-link" className="underline hover:text-[#3A2921]">Política de Privacidade</a>
          <span>•</span>
          <button type="button" data-testid="footer-cookie-preferences" onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))} className="underline hover:text-[#3A2921]">Preferências de cookies</button>
        </p>
      </footer>

      {/* ===== STICKY MOBILE CTA ===== */}
      <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-[#FDFBF7]/95 backdrop-blur border-t border-[#E5DCCF] px-4 py-3 shadow-[0_-4px_20px_rgba(58,41,33,0.10)]">
        <div className="flex items-center justify-between gap-3">
          <div className="leading-tight">
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-[#B9A48F] line-through">{OLD_PRICE}</span>
              <span className="font-serif-display text-lg font-semibold text-[#3A2921]">{price}</span>
            </div>
            <div className="text-[10px] text-[#8C7565] uppercase tracking-wide">pagamento único</div>
          </div>
          <CTA href={checkoutUrl} testid="v3-cta-sticky" label="QUERO COMEÇAR" className="px-5 py-3 text-sm flex-1 max-w-[220px]" />
        </div>
      </div>
    </div>
  );
}
